import {
  Badge,
  Box,
  Button,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  Text,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { createContext, useCallback, useContext } from 'react';
import { FaPencil } from 'react-icons/fa6';
import SubscriptionsContext from './SubscriptionsContext';
import SubscriptionTab from '../components/SubscriptionTab';

type SubscriptionManagementContextType = {
  canManageSubscription: boolean;
  openManager: () => void;
};

const SubscriptionManagementContext = createContext<SubscriptionManagementContextType>({
  canManageSubscription: false,
  openManager: () => {},
});

const SubscriptionManagementProvider = ({ children }: React.PropsWithChildren): JSX.Element => {
  const { customerSubscription } = useContext(SubscriptionsContext);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const openManager = useCallback(() => {
    if (!customerSubscription) return;
    onOpen();
  }, [customerSubscription, onOpen]);

  return (
    <SubscriptionManagementContext.Provider
      value={{
        canManageSubscription: !!customerSubscription,
        openManager,
      }}
    >
      {children}

      <Modal isOpen={isOpen} onClose={onClose} size={['full', 'xl', '2xl']} isCentered>
        <ModalOverlay />
        <ModalContent mx={[0, 4]} maxH="90vh">
          <ModalHeader>Palomma Plus</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" pb={6}>
            <SubscriptionTab />
          </ModalBody>
        </ModalContent>
      </Modal>
    </SubscriptionManagementContext.Provider>
  );
};

type SubscriptionManagementTriggerProps =
  | {
      variant?: 'button';
      label: string;
      showIfUnavailable?: boolean;
    }
  | {
      variant: 'icon';
      label?: string;
      showIfUnavailable?: boolean;
    };

const SubscriptionManagementTrigger = ({
  variant = 'button',
  label = 'Administrar Palomma Plus',
  showIfUnavailable = false,
}: SubscriptionManagementTriggerProps): JSX.Element | null => {
  const { canManageSubscription, openManager } = useContext(SubscriptionManagementContext);

  if (!canManageSubscription && !showIfUnavailable) return null;

  if (variant === 'icon') {
    return (
      <IconButton
        aria-label="Administrar Palomma Plus"
        icon={<FaPencil />}
        type="button"
        variant="unstyled"
        size="xs"
        color={'gray.500'}
        display={'flex'}
        alignItems={'center'}
        minW="auto"
        _hover={{ color: 'gray.700' }}
        onClick={openManager}
      />
    );
  }

  return (
    <Button variant="outline" colorScheme="purple" onClick={openManager}>
      {label}
    </Button>
  );
};

const SubscriptionManagementCard = (): JSX.Element | null => {
  const { canManageSubscription, openManager } = useContext(SubscriptionManagementContext);
  const { customerSubscription } = useContext(SubscriptionsContext);

  if (!canManageSubscription || !customerSubscription) return null;

  return (
    <Box border="1px solid" borderColor="#DFDEE2" borderRadius="10px" bg="gray.50" px={4} py={3}>
      <Box
        display="flex"
        alignItems={{ base: 'flex-start' }}
        justifyContent="space-between"
        gap={3}
        flexDirection={{ base: 'column' }}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Text fontWeight={600}>Palomma Plus</Text>
            <Badge colorScheme="green" fontSize="0.7rem">
              Activa
            </Badge>
          </Box>
          <Text fontSize="sm" color="gray.600">
            Gestiona tu suscripción, revisa tus Puntos Colombia o cancélala antes de tu próximo cobro.
          </Text>
        </Box>

        <Button variant="outline" colorScheme="purple" onClick={openManager} minW={{ base: '100%' }}>
          Administrar Palomma Plus
        </Button>
      </Box>
    </Box>
  );
};

export default SubscriptionManagementContext;
export { SubscriptionManagementProvider, SubscriptionManagementTrigger, SubscriptionManagementCard };
